import React, { useState, useEffect } from 'react';
import { X, Heart, Send, MessageCircle, Share2, Bookmark } from 'lucide-react';
import { AppUser } from '../../../types';
import { saveComment, hasMediaCommentLike, toggleMediaCommentLike } from '../../../backend/mediaService';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import { db } from '../../../backend/firebase';
import { subscribeToBlockedUsers } from '../../../backend/moderationService';
import { recordReadWriteCount } from '../../../utils/withReadCounter';

interface Comment {
  id: string;
  userId: string;
  userName: string;
  userAvatar: string;
  text: string;
  createdAt: any;
  likes: number;
  likesCount?: number;
  isLiked?: boolean;
  repliesCount: number;
}

interface InstagramCommentsProps {
  isOpen: boolean;
  onClose: () => void;
  mediaId: string;
  mediaType: 'photo' | 'short' | 'long' | 'live';
  currentUser: AppUser | null;
  onOpenAuth: () => void;
  authorName: string;
  likesCount: number;
  isMediaLiked?: boolean;
  onLikeToggle?: () => void;
  timeAgo: string;
  onOpenProfile?: (uid: string) => void;
}

export const InstagramComments: React.FC<InstagramCommentsProps> = ({
  isOpen,
  onClose,
  mediaId,
  mediaType,
  currentUser,
  onOpenAuth,
  authorName,
  likesCount,
  isMediaLiked,
  onLikeToggle,
  timeAgo,
  onOpenProfile
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [localLikesCount, setLocalLikesCount] = useState(likesCount);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [blockedUsersReady, setBlockedUsersReady] = useState(!currentUser?.uid);
  const [blockedUsersError, setBlockedUsersError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [likeInFlightIds, setLikeInFlightIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setLocalLikesCount(likesCount);
  }, [likesCount, mediaId]);

  useEffect(() => {
    setIsLiked(isMediaLiked ?? false);
  }, [mediaId, isMediaLiked]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      setBlockedUsersReady(true);
      return;
    }
    setBlockedUsersReady(false);
    setBlockedUsersError('');
    return subscribeToBlockedUsers(currentUser.uid, blocked => {
      setBlockedUserIds(blocked);
      setBlockedUsersReady(true);
    }, error => {
      setBlockedUsersError(error.message);
      setBlockedUsersReady(true);
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!isOpen || !mediaId || !blockedUsersReady || blockedUsersError) {
      setComments([]);
      return;
    }

    const q = query(
      collection(db, 'media', mediaId, 'comments'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsubscribe = onSnapshot(q, snapshot => {
      recordReadWriteCount('media-comments', {
        reads: snapshot.metadata.fromCache ? 0 : snapshot.docChanges().length
      });
      void Promise.all(snapshot.docs.map(async commentDoc => {
        const comment = { id: commentDoc.id, ...commentDoc.data() } as Comment;
        comment.likes = comment.likesCount ?? comment.likes ?? 0;
        comment.isLiked = currentUser
          ? await hasMediaCommentLike(mediaId, comment.id, currentUser.uid, 'media-comments')
          : false;
        return comment;
      })).then(list => {
        setComments(list.filter(comment => !currentUser || !blockedUserIds.has(comment.userId)));
      }).catch(error => {
        console.error('Could not load comment likes:', error);
        setBlockedUsersError('Could not load comment likes.');
      });
    });

    return () => unsubscribe();
  }, [isOpen, mediaId, blockedUsersReady, blockedUsersError, blockedUserIds, currentUser?.uid]);

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (!inputText.trim()) return;

    const text = inputText.trim();
    setCommentError('');
    setInputText('');

    try {
      await saveComment(mediaId, currentUser.uid, text, currentUser.displayName);
    } catch (err) {
      console.error('Comment failed:', err);
      setInputText(text);
      setCommentError(err instanceof Error ? err.message : 'Could not post your comment.');
    }
  };

  const toggleCommentLike = async (commentId: string) => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (likeInFlightIds.has(commentId)) return;
    setLikeInFlightIds(previous => new Set(previous).add(commentId));
    const previous = comments;
    const wasLiked = previous.find(comment => comment.id === commentId)?.isLiked ?? false;
    setComments(previous.map(comment =>
      comment.id === commentId
        ? { ...comment, isLiked: !wasLiked, likes: Math.max(0, comment.likes + (wasLiked ? -1 : 1)) }
        : comment
    ));
    setCommentError('');
    try {
      const isLikedNow = await toggleMediaCommentLike(mediaId, commentId, currentUser.uid);
      if (isLikedNow === wasLiked) {
        setComments(value => value.map(comment =>
          comment.id === commentId
            ? { ...comment, isLiked: isLikedNow, likes: Math.max(0, comment.likes + (isLikedNow ? 1 : -1)) }
            : comment
        ));
      }
    } catch (error) {
      setComments(previous);
      setCommentError(error instanceof Error ? error.message : 'Could not update comment like.');
    } finally {
      setLikeInFlightIds(previousIds => {
        const next = new Set(previousIds);
        next.delete(commentId);
        return next;
      });
    }
  };

  const formatDateTime = (date: any) => {
    if (!date) return 'Sending...';
    const value = typeof date?.toDate === 'function' ? date.toDate() : new Date(date);
    return Number.isNaN(value.getTime()) ? 'Sending...' : value.toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2rem] w-full max-w-md h-[85vh] sm:h-[600px] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="flex-1" />
          <h3 className="font-black text-sm uppercase tracking-widest text-slate-900">Comments</h3>
          <div className="flex-1 flex justify-end">
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-50 transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Post Summary Action Bar (Section 11.4) */}
        <div className="px-5 py-4 border-b border-slate-50 space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                      type="button"
                      aria-label={isLiked ? 'Unlike media' : 'Like media'}
                      aria-pressed={isLiked}
                      onClick={() => {
                        if (!currentUser) {
                          onOpenAuth();
                        } else if (onLikeToggle) {
                          onLikeToggle();
                        } else {
                          setIsLiked(!isLiked);
                          setLocalLikesCount(previous => Math.max(0, previous + (isLiked ? -1 : 1)));
                        }
                      }}
                      className="active:scale-125 transition-transform"
                    >
                        <Heart className={`w-6 h-6 ${isLiked ? 'text-rose-500 fill-rose-500' : 'text-slate-800'}`} />
                    </button>
                    <MessageCircle className="w-6 h-6 text-slate-800" />
                    <Share2 className="w-6 h-6 text-slate-800" />
                </div>
                <Bookmark className="w-6 h-6 text-slate-800" />
            </div>
            <div className="space-y-0.5">
                <p className="text-xs font-black text-slate-900">
                    {localLikesCount.toLocaleString()} likes
                </p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                    {timeAgo}
                </p>
            </div>
        </div>

        {/* Comments List (Section 11.1 & 11.2) */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-5 space-y-6">
          {(blockedUsersError || commentError) && <p role="alert" className="text-xs text-rose-600">{commentError || 'Could not load comments.'}</p>}
          {comments.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-30">
               <div className="w-20 h-20 rounded-full border-2 border-slate-200 flex items-center justify-center">
                  <MessageCircle className="w-10 h-10" />
               </div>
               <p className="text-sm font-black uppercase tracking-widest">No comments yet</p>
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="flex items-start gap-3">
                <img
                    src={comment.userAvatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                    className={`w-8 h-8 rounded-full object-cover shrink-0 border border-slate-100 ${onOpenProfile ? 'cursor-pointer' : ''}`}
                    alt={comment.userName}
                    onClick={() => onOpenProfile?.(comment.userId)}
                />
                <div className="flex-1 space-y-1">
                    <p className="text-xs text-slate-900 leading-snug">
                        <span
                          className={`font-black mr-2 ${onOpenProfile ? 'cursor-pointer hover:underline' : ''}`}
                          onClick={() => onOpenProfile?.(comment.userId)}
                        >
                          @{comment.userName}
                        </span>
                        {comment.text}
                    </p>
                    <div className="flex items-center gap-4 text-[10px] font-bold text-slate-400">
                        <span>{formatDateTime(comment.createdAt)}</span>
                        {comment.likes > 0 && <span>{comment.likes} likes</span>}
                        <button className="hover:text-slate-600">Reply</button>
                    </div>
                    {comment.repliesCount > 0 && (
                        <div className="pt-2 flex items-center gap-3">
                            <div className="w-8 h-[1px] bg-slate-200" />
                            <button className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                                View replies ({comment.repliesCount})
                            </button>
                        </div>
                    )}
                </div>
                <button
                  type="button"
                  aria-pressed={comment.isLiked}
                  disabled={likeInFlightIds.has(comment.id)}
                  onClick={() => void toggleCommentLike(comment.id)}
                  className="pt-1 active:scale-125 transition-transform"
                >
                    <Heart className={`w-3.5 h-3.5 ${comment.isLiked ? 'text-rose-500 fill-rose-500' : 'text-slate-300 hover:text-rose-500'}`} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Comment Input (Section 11.5) */}
        <div className="p-4 border-t border-slate-100 bg-white pb-safe">
          <form onSubmit={handlePostComment} className="flex items-center gap-3">
            <img
                src={currentUser?.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100'}
                className="w-9 h-9 rounded-full object-cover border border-slate-100 shadow-sm"
                alt="Me"
            />
            <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  placeholder="Add a comment..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-2.5 text-xs font-medium focus:outline-none focus:border-blue-900 focus:bg-white transition-all"
                />
            </div>
            <button
              type="submit"
              disabled={!inputText.trim()}
              className={`text-xs font-black uppercase tracking-widest ${inputText.trim() ? 'text-blue-600' : 'text-blue-300 pointer-events-none'}`}
            >
              Post
            </button>
          </form>
          {commentError && <p role="alert" className="mt-2 text-xs text-rose-600">{commentError}</p>}
        </div>
      </div>
    </div>
  );
};
